import React from 'react';
import { useTheme } from '@mui/material';
import { artworkFor } from '../../configuration/artwork';

/**
 * The ring from the corporate identity, placed as the artwork itself rather
 * than rebuilt in code — see GoalsCircleDrawn.tsx for the rebuild, kept
 * alongside.
 *
 * The three goals as the ring, with their labels painted into the picture: they
 * stay German on the English pages and stay put when an editor renames a goal
 * at /admin — visible on the page rather than hidden. It does follow the light
 * and dark views, through the white export listed in configuration/artwork.ts.
 *
 * Which file this is has to be changed here as well as there: artwork.ts only
 * says what the white counterpart of a drawing is, and it is this line that
 * decides which drawing the section places at all.
 *
 * What still follows the content is the name the picture is announced under: it
 * is built from the goals as they are actually configured, so a reader who
 * cannot see the ring hears the current labels in the current language, and
 * hears a fourth goal that the artwork has no room for.
 */

interface Goal {
  key: string;
  label: string;
}

interface GoalsCircleProps {
  goals: Goal[];
  /** Names the drawing for screen readers; the section heading in practice. */
  title: string;
}

/** The artwork's own pixel size, so the browser can hold its place while it loads. */
const RING = { src: '/Ziele.png', width: 902, height: 734 };

export const GoalsCircle: React.FC<GoalsCircleProps> = ({ goals, title }) => {
  const mode = useTheme().palette.mode;

  return (
    <img
      src={artworkFor(RING.src, mode)}
      alt={`${title}: ${goals.map((goal) => goal.label).join(', ')}`}
      width={RING.width}
      height={RING.height}
      loading="lazy"
      decoding="async"
      // Capped rather than given a width: past this the labels painted into it
      // grow larger than the heading above them.
      style={{ display: 'block', width: '100%', maxWidth: 420, height: 'auto', margin: '0 auto' }}
    />
  );
};
